import React from 'react';
import {
    useSsDispatch as useDispatch,
    useSsSelector as useSelector,
} from '~SpectateStore';
import classNames from 'classnames';

import i18n from '~shared/utils/i18n';
import formatCurrency from '~shared/utils/currencyUtils';

import {
    openLoginModal,
    openDepositWidget,
    openGeoCheckRetryModal,
    setSiteScrollDisabled,
} from '~spa/Utils/WrapperIntegration/Ucf';

import {
    selectParlayTabTotalStake,
    selectSinglesTabTotalStake,
} from '~Betslip/selectors/totalStakeSelectors';

import {
    selectMultiplesTotalStake,
    selectMultiplesTotalPayout,
} from '~Betslip/selectors/userMultiplesSelectors';

import {
    selectDisplaySinglesContent,
    selectDisplayParlayContent,
    selectDisplayMultiplesContent,
    selectIsPlacingBet,
    selectDisplayFullslip,
    selectBetslipGTMComponent,
} from '~Betslip/selectors/uiSelectors';

import {
    clearBetslip,
} from '~Betslip/interface/interactions';

import {
    placeBet,
} from '~Betslip/thunks/placeBet';

import {
    selectSinglesTabTotalPayout,
    selectParlayTabTotalPayout,
} from '~Betslip/selectors/totalPayoutSelectors';

import {
    selectAnySelectionSuspended,
    selectCountSelectionDetails,
    selectCountSuspendedSelections,
} from '~Betslip/selectors/selectionDetailsSelectors';

import GeoLocationCheck from '~Betslip/uiModes/Full/Components/GeoLocationCheck';
import {
    selectHasTotalSinglesExceedBalance,
    selectHasParlayExceedBalance,
    selectHasMultiplesExceedBalance,
    selectShouldShowReturns,
    selectShouldShowOpenBetslip,
} from '~Betslip/selectors/userSelectors';

import {
    useIsGeoCheckFailure,
    useIsGeoCheckInProgress,
    useIsUserLoggedIn,
    useShouldHideLogin,
    useUserBalance,
    useUserTaxOnReturns,
} from '~SpectateStore/hooks/userHooks';

import {
    hasZeroBalance,
} from '~Betslip/utils/depositChecks';

import {
    setFullScreenOn,
} from '~Betslip/slices/uiSlice';

import './styles.scss';

const TabStake = ({stake}) => {
    return formatCurrency(stake);
};

const BetslipCTAs = () => {
    const isSinglesContent = useSelector(selectDisplaySinglesContent);
    const isParlayContent = useSelector(selectDisplayParlayContent);
    const isMultiplesContent = useSelector(selectDisplayMultiplesContent);
    const isPlacingBet = useSelector(selectIsPlacingBet);

    const singlesTotalStake = useSelector(selectSinglesTabTotalStake);
    const parlayTabTotalStake = useSelector(selectParlayTabTotalStake);
    const multiplesTotalStake = useSelector(selectMultiplesTotalStake);

    const singlesTotalPayout = useSelector(selectSinglesTabTotalPayout);
    const parlayTotalPayout = useSelector(selectParlayTabTotalPayout);
    const multiplesTotalPayout = useSelector(selectMultiplesTotalPayout);

    const isAnySelectionSuspended = useSelector(selectAnySelectionSuspended);
    const isMultiBetDisabled = isMultiplesContent && isAnySelectionSuspended;
    const countSuspendedSelections = useSelector(selectCountSuspendedSelections);
    const countTotalSelections = useSelector(selectCountSelectionDetails);
    const isBetButtonDisabled = countSuspendedSelections === countTotalSelections;
    const isGeoCheckFailure = useIsGeoCheckFailure();
    const isGeoCheckInProgress = useIsGeoCheckInProgress();

    const loggedIn = useIsUserLoggedIn();
    const showLogin = !useShouldHideLogin();
    const showReturns = useSelector(selectShouldShowReturns);
    const tax = useUserTaxOnReturns();
    const dispatch = useDispatch();
    const taxForDisplay = tax * 100;
    const displayFullslip = useSelector(selectDisplayFullslip);
    const showTaxMessage = tax && displayFullslip;

    const singlesBalance = useSelector(selectHasTotalSinglesExceedBalance);
    const parlayBalance = useSelector(selectHasParlayExceedBalance);
    const multiplesBalance = useSelector(selectHasMultiplesExceedBalance);
    const userBalance = useUserBalance();
    const isEmptyBalance = hasZeroBalance(userBalance);
    const showOpenBabyslip = useSelector(selectShouldShowOpenBetslip);

    const gtmComponent = useSelector(selectBetslipGTMComponent);

    const taxDiscount = () => {
        const discount = tax * Number(totalPayout());
        if (isNaN(discount)) {
            return '';
        }

        return ` (${formatCurrency(discount)})`;
    };

    const isFreeBetSelected = () => {
        if ((isSinglesContent && singlesBalance.freebetSelected) ||
            (isParlayContent && parlayBalance.freebetSelected) ||
            (isMultiplesContent && multiplesBalance.freebetSelected)
        ) {
            return true;
        }

        return false;
    };

    const checkDisabledCTA = () => {
        if (showOpenBabyslip) {
            return false;
        }

        if (!loggedIn && showLogin) {
            return false;
        }

        if (isGeoCheckFailure) {
            return false;
        }

        if (isGeoCheckInProgress) {
            return true;
        }

        if (checkDepositNeeded()) {
            return false;
        }

        if (isMultiBetDisabled || isBetButtonDisabled || !showLogin) {
            return true;
        }

        return ((isSinglesContent && singlesTotalStake === 0) ||
            (isParlayContent && parlayTabTotalStake === 0) ||
            (isMultiplesContent && multiplesTotalStake === 0)
        );
    };

    const checkDepositNeeded = () => {
        if (isEmptyBalance && !isFreeBetSelected()) {
            return true;
        }

        if ((isSinglesContent && singlesBalance.isDepositNeeded) ||
            (isParlayContent && parlayBalance.isDepositNeeded) ||
            (isMultiplesContent && multiplesBalance.isDepositNeeded)) {

            return true;
        }

        return false;
    };

    const ctaClasses = classNames('fullslip__cta fullslip__cta--place-bet', {
        'fullslip__cta--disabled': checkDisabledCTA(),
        'fullslip__cta--loading': isPlacingBet,
    });

    const onClickHandler = () => {
        if (checkDisabledCTA()) {
            return;
        }

        if (!loggedIn && showLogin) {
            openLoginModal();
        } else if (isGeoCheckFailure) {
            openGeoCheckRetryModal();
        } else if (checkDepositNeeded()) {
            openDepositWidget();
        } else if (loggedIn && showOpenBabyslip) {
            dispatch(setFullScreenOn());
            setSiteScrollDisabled(true);
        } else if (!isPlacingBet) {
            dispatch(placeBet());
        }
    };

    const totalPayout = () => {
        if (isSinglesContent) {
            return singlesTotalPayout;
        }
        if (isParlayContent) {
            return parlayTotalPayout;
        }
        if (isMultiplesContent) {
            return !isNaN(multiplesTotalPayout) ? multiplesTotalPayout : i18n.t('betslip.na');
        }

        return null;
    };

    const totalDeposit = () => {
        if (isSinglesContent && singlesBalance.isDepositNeeded) {
            return singlesBalance.depositAmount;
        }
        if (isParlayContent && parlayBalance.isDepositNeeded) {
            return parlayBalance.depositAmount;
        }
        if (isMultiplesContent && multiplesBalance.isDepositNeeded) {
            return multiplesBalance.depositAmount;
        }

        return 0;
    };

    const ctaGTMTag = () => {
        if (!loggedIn && showLogin) {
            return `${gtmComponent};login-and-place-bet`;
        }

        if (showOpenBabyslip) {
            return `${gtmComponent};show-betslip`;
        }

        if (isGeoCheckFailure) {
            return `${gtmComponent};locationnotverified`;
        }

        if (checkDepositNeeded()) {
            return `${gtmComponent};deposit`;
        }

        let tab = 'singles';
        if (isParlayContent) {
            tab = 'parlay';
        }
        if (isMultiplesContent) {
            tab = 'round-robin';
        }

        return `${gtmComponent};place-bet;${tab}`;
    };

    const renderTaxIndicator = () => {
        if (tax && !displayFullslip) {
            return <>
                <span className="babyslip__tax-indicator">
                    {`-${tax * 100}%`}
                </span>
            </>;
        }

        return null;
    };

    const renderDepositContent = () => {
        if (isEmptyBalance) {

            return <>
                <span className="fullslip__cta--deposit" data-test-id="betslip-footer;deposit-betslip">
                    {i18n.t('betslip.deposit')}
                    {' '}
                </span>
                <span data-test-id="betslip-footer;deposit-required">
                    {i18n.t('betslip.balance')}
                    {' '}
                    {formatCurrency(0)}
                </span>
            </>;
        }

        return <>
            <span className="fullslip__cta--deposit" data-test-id="betslip-footer;deposit-betslip">
                {i18n.t('betslip.deposit')}
                {' '}
            </span>
            <span data-test-id="betslip-footer;deposit-required">
                {i18n.t('betslip.deposit_required', {deposit_amount: formatCurrency(totalDeposit())})}
            </span>
        </>;
    };

    const renderPlaceButtonContent = () => {

        if (isGeoCheckInProgress || isGeoCheckFailure) {
            return <GeoLocationCheck />;
        }

        if (checkDepositNeeded()) {
            return renderDepositContent();
        }

        if (showOpenBabyslip) {
            return <span className="fullslip__cta--show-betslip">
                {i18n.t('betslip.show_betslip')}
            </span>;
        }

        return isPlacingBet
        ? <span>{i18n.t('betslip.placing_bet')}</span>
        : <>
            <span data-test-id="betslip-footer;total-stake" dir="auto">
                {i18n.t('betslip.place_bet')}
                {' '}
                <span dir="auto">
                    {isSinglesContent ? <TabStake stake={singlesTotalStake}/> : null}
                    {isParlayContent ? <TabStake stake={parlayTabTotalStake}/> : null}
                    {isMultiplesContent ? <TabStake stake={multiplesTotalStake}/> : null}
                </span>
            </span>
            <span data-test-id="betslip-footer;potential-returns" className="fullslip__cta--place-bet-return" dir="auto">
                {i18n.t('betslip.payout')}
                {' '}
                <span dir="auto">
                    {formatCurrency(totalPayout())}
                </span>
                {renderTaxIndicator()}
            </span>
        </>;
    };

    const renderLoginButton = () => {
        return <>
            <span className="fullslip__cta--login">{i18n.t('betslip.login')}</span>
            {
                showReturns
                ? <span data-test-id="betslip-footer;potential-returns" className="fullslip__cta--place-bet-return" dir="auto">
                    {i18n.t('betslip.payout')}
                    {' '}
                    <span dir="auto">{formatCurrency(totalPayout())}</span>
                    {renderTaxIndicator()}
                </span>
                : null
            }
        </>;
    };

    return (
        <>
            <div className="fullslip__ctas">
                <div
                    className="fullslip__cta fullslip__cta--clear"
                    data-spectate-product={`${gtmComponent};remove-all-selections`}
                    onClick={() => {
                        clearBetslip();
                    }}
                    data-test-id="betslip;clear"
                >
                    <i className="fullslip__trash-icon fullslip__trash-icon--large"></i>
                </div>
                <div
                    className={ctaClasses}
                    data-spectate-product={ctaGTMTag()}
                    data-test-id="betslip;place-bet"
                    onClick={onClickHandler}
                >
                    {
                        (!loggedIn && showLogin)
                        ? renderLoginButton()
                        : renderPlaceButtonContent()
                    }
                </div>
            </div>
            {showTaxMessage
            ? <div className="fullslip__tax-message">
                {i18n.t('betslip.tax_on_return_message', {
                    tax: taxForDisplay,
                    discount: taxDiscount(),
                })}
            </div>
            : null}
        </>
    );
};

export default BetslipCTAs;
